import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, Box, Typography, TextField, Button, Chip, Stack,
  Divider, Grid, FormControlLabel, Checkbox, Skeleton, alpha,
  ToggleButton, ToggleButtonGroup, Alert, IconButton, useMediaQuery, useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AppDatePicker from "../../../Components/DatePicker/AppDatePicker";
import AddTaskOutlinedIcon from "@mui/icons-material/AddTaskOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import SmartDisplayOutlinedIcon from "@mui/icons-material/SmartDisplayOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import {
  useGetMyAssessmentOptionsQuery,
  useCreateMyAssessmentMutation,
} from "../../../Api/attoDeskApi";

/**
 * Staff-only "New Assessment" modal.
 *
 * Visually mirrors the admin CreateAssessmentWizard (navy gradient
 * header, sectioned body, navy primary in the footer) so the two
 * surfaces feel like the same product family. Data-wise this dialog
 * NEVER hits admin endpoints:
 *
 *   - Options load from `/api/staff-portal/assessments/options`
 *     (StaffTeachingScopeService-narrowed)
 *   - Create posts to `/api/staff-portal/assessments` which forces
 *     `staff_id = Auth::user()->staffId` and re-validates every id.
 *
 * The admin wizard, admin routes, and admin RTK hooks are not
 * imported here — extraction risk is zero because the admin file
 * (`Pages/Assessments/CreateAssessmentWizard.tsx`) is not referenced.
 */

export type TeachingPair = {
  grade_id: number;
  grade_name: string;
  subject_id: number;
  subject_name: string;
  subject_grade_id: number;
  students: Array<{ id: number; name: string; initials: string; grade: string | null }>;
};

export type AssessmentType = {
  value: "test" | "quiz" | "homework" | "observation" | "project" | "other";
  label: string;
  numeric: boolean;
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const NAVY = "#1b3a6b";
const NAVY_2 = "#2a5298";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: (message: string) => void;
  /** Optional deep-link prefill (from ?compose=1&student_id=42). */
  prefillStudentId?: number | null;
};

const StaffAssessmentDialog: React.FC<Props> = ({ open, onClose, onCreated, prefillStudentId }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: optsResp, isLoading: optionsLoading, isError: optionsError } =
    useGetMyAssessmentOptionsQuery(undefined, { skip: !open });
  const [createAssessment, { isLoading: submitting }] = useCreateMyAssessmentMutation();

  const teaching: TeachingPair[] = useMemo(
    () => ((optsResp as any)?.data?.teaching ?? []) as TeachingPair[],
    [optsResp],
  );
  const types: AssessmentType[] = useMemo(
    () => ((optsResp as any)?.data?.assessment_types ?? []) as AssessmentType[],
    [optsResp],
  );

  const [pairKey, setPairKey] = useState<string>("");
  const [selectedStudentIds, setSelectedStudentIds] = useState<number[]>([]);
  const [title, setTitle] = useState("");
  const [assessmentType, setAssessmentType] = useState<AssessmentType["value"] | "">("");
  const [date, setDate] = useState<string>(todayIso());
  const [dueDate, setDueDate] = useState<string>("");
  const [worth, setWorth] = useState<string>("20");
  const [passThreshold, setPassThreshold] = useState<string>("60");
  const [focus, setFocus] = useState("");
  const [instructions, setInstructions] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  // Learning Materials state (spec §9–§13)
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [youtubeUrl, setYoutubeUrl] = useState<string>("");
  const [dropActive, setDropActive] = useState<boolean>(false);
  // Grade → subject two-step selector state derived from the pair.
  const [selectedGradeId, setSelectedGradeId] = useState<number | null>(null);

  const currentPair = useMemo(
    () => teaching.find((p) => `${p.grade_id}:${p.subject_id}` === pairKey) ?? null,
    [teaching, pairKey],
  );

  // ── STEP 1: available grades (dedup, canonical order) ──────
  // Canonical order matches the schoolwide vocabulary — pre-K first,
  // then JK/SK, then numbered 1..12, then Adult, then any unknown.
  const CANONICAL_GRADE_ORDER: string[] = [
    "pre-k", "prek", "pre k",
    "jk", "sk",
    "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12",
    "adult",
  ];
  const gradeSortKey = (name: string): number => {
    const n = String(name || "").trim().toLowerCase();
    // strip a leading "grade " so "Grade 3" and "3" sort together
    const stripped = n.replace(/^grade\s+/, "");
    const idx = CANONICAL_GRADE_ORDER.indexOf(stripped);
    return idx === -1 ? 999 : idx;
  };
  const availableGrades = useMemo(() => {
    const seen = new Map<number, { id: number; name: string }>();
    for (const p of teaching) {
      if (!seen.has(p.grade_id)) seen.set(p.grade_id, { id: p.grade_id, name: p.grade_name || `Grade #${p.grade_id}` });
    }
    return Array.from(seen.values()).sort((a, b) => {
      const ka = gradeSortKey(a.name); const kb = gradeSortKey(b.name);
      if (ka !== kb) return ka - kb;
      return a.name.localeCompare(b.name);
    });
  }, [teaching]);

  // ── STEP 2: subjects for the selected grade (dedup) ─────────
  const availableSubjects = useMemo(() => {
    if (selectedGradeId == null) return [];
    const seen = new Map<number, { id: number; name: string; pairKey: string }>();
    for (const p of teaching) {
      if (p.grade_id !== selectedGradeId) continue;
      if (!seen.has(p.subject_id)) {
        seen.set(p.subject_id, {
          id: p.subject_id,
          name: p.subject_name || `Subject #${p.subject_id}`,
          pairKey: `${p.grade_id}:${p.subject_id}`,
        });
      }
    }
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [teaching, selectedGradeId]);
  const selectedSubjectId = currentPair?.subject_id ?? null;

  const chooseGrade = (gid: number) => {
    if (gid === selectedGradeId) return;
    setSelectedGradeId(gid);
    // Changing grade invalidates any previously chosen subject/students.
    setPairKey("");
    setSelectedStudentIds([]);
    setErrors((e) => ({ ...e, pair: "", student_ids: "" }));
  };
  const chooseSubject = (nextPairKey: string) => {
    if (nextPairKey === pairKey) return;
    setPairKey(nextPairKey);
    setSelectedStudentIds([]);
    setErrors((e) => ({ ...e, pair: "", student_ids: "" }));
  };
  const currentType = useMemo(
    () => types.find((t) => t.value === assessmentType) ?? null,
    [types, assessmentType],
  );

  // Reset when the dialog opens fresh, and apply prefill (deep link
  // from Student Detail / Today etc). Backend still re-checks scope
  // so a malicious student_id in the URL never grants access.
  useEffect(() => {
    if (!open) return;
    setPairKey("");
    setSelectedStudentIds([]);
    setTitle("");
    setAssessmentType("");
    setDate(todayIso());
    setDueDate("");
    setWorth("20");
    setPassThreshold("60");
    setFocus("");
    setInstructions("");
    setErrors({});
    setServerError(null);
    setAttachedFiles([]);
    setYoutubeUrl("");
    setSelectedGradeId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !prefillStudentId || teaching.length === 0 || pairKey) return;
    const pair = teaching.find((p) => p.students.some((s) => s.id === prefillStudentId));
    if (pair) {
      setSelectedGradeId(pair.grade_id);
      setPairKey(`${pair.grade_id}:${pair.subject_id}`);
      setSelectedStudentIds([prefillStudentId]);
    }
  }, [open, prefillStudentId, teaching, pairKey]);

  // (former changePair helper replaced by chooseGrade/chooseSubject.)

  const toggleStudent = (id: number) =>
    setSelectedStudentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  const selectAllStudents = () => currentPair && setSelectedStudentIds(currentPair.students.map((s) => s.id));
  const clearStudents = () => setSelectedStudentIds([]);

  // ── Learning Materials helpers ─────────────────────────────
  const ALLOWED_EXTS = ["pdf", "doc", "docx", "ppt", "pptx", "xls", "xlsx", "txt", "jpg", "jpeg", "png", "webp", "gif"];
  const MAX_BYTES = 10 * 1024 * 1024; // 10 MB
  const handlePickFiles = (picked: File[]) => {
    if (!picked || picked.length === 0) return;
    const kept: File[] = [];
    let rejection: string | null = null;
    for (const f of picked) {
      const ext = (f.name.split(".").pop() || "").toLowerCase();
      if (!ALLOWED_EXTS.includes(ext)) {
        rejection = "Unsupported file type.";
        continue;
      }
      if (f.size > MAX_BYTES) {
        rejection = "File exceeds the 10 MB limit.";
        continue;
      }
      kept.push(f);
    }
    if (kept.length > 0) setAttachedFiles((prev) => [...prev, ...kept]);
    setErrors((e) => ({ ...e, attachments: rejection ?? "" }));
  };
  const fileIcon = (f: File) => {
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (ext === "pdf") return <PictureAsPdfOutlinedIcon fontSize="small" />;
    if (["jpg", "jpeg", "png", "webp", "gif"].includes(ext)) return <ImageOutlinedIcon fontSize="small" />;
    return <InsertDriveFileOutlinedIcon fontSize="small" />;
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!currentPair) e.pair = "Choose a grade and subject you teach.";
    if (currentPair && selectedStudentIds.length === 0) e.student_ids = "Select at least one student.";
    if (!title.trim()) e.title = "Assessment title is required.";
    if (!assessmentType) e.assessment_type = "Select an assessment type.";
    if (!date) e.date = "Assessment date is required.";
    const worthNum = Number(worth);
    if (!Number.isFinite(worthNum) || worthNum < 1 || worthNum > 1000)
      e.worth = "Maximum score must be between 1 and 1000.";
    if (currentType?.numeric) {
      const pt = Number(passThreshold);
      if (passThreshold !== "" && (!Number.isFinite(pt) || pt < 0 || pt > 100))
        e.pass_threshold = "Pass threshold must be between 0 and 100.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    setServerError(null);
    if (!validate() || !currentPair || !assessmentType) return;
    try {
      const body: Parameters<typeof createAssessment>[0] = {
        grade_id: currentPair.grade_id,
        subject_id: currentPair.subject_id,
        student_ids: selectedStudentIds,
        title: title.trim(),
        assessment_type: assessmentType,
        date,
        worth: Number(worth),
      };
      if (dueDate) body.due_date = dueDate;
      if (currentType?.numeric && passThreshold !== "") body.pass_threshold = Number(passThreshold);
      const trimmedFocus = focus.trim();
      if (trimmedFocus) body.focus = trimmedFocus;
      const trimmedInstr = instructions.trim();
      if (trimmedInstr) body.instructions = trimmedInstr;
      // Learning Materials — backend re-validates extension/MIME/size
      // and YouTube host allow-list; the UI just prevents obviously
      // wrong submissions.
      if (attachedFiles.length > 0) (body as any).attachments = attachedFiles;
      const trimmedYt = youtubeUrl.trim();
      if (trimmedYt) (body as any).youtube_url = trimmedYt;

      await createAssessment(body).unwrap();
      onCreated("Assessment created successfully.");
    } catch (err: any) {
      const status = err?.status ?? err?.originalStatus;
      const message = err?.data?.message;
      const fieldErrors = err?.data?.errors;
      if (fieldErrors && typeof fieldErrors === "object") {
        // Map Laravel's raw column names to the labels the teacher
        // actually sees on the form. Without this, a validation
        // failure produces "The worth field is required." which is
        // both meaningless to the user and looks like a bug.
        const LABELS: Record<string, string> = {
          grade_id: "Grade",
          subject_id: "Subject",
          student_ids: "Students",
          "student_ids.0": "Students",
          title: "Assessment title",
          assessment_type: "Assessment type",
          date: "Assessment date",
          due_date: "Due date",
          worth: "Maximum score",
          pass_threshold: "Pass %",
          focus: "Focus / Learning goal",
          instructions: "Instructions",
          attachments: "Attachments",
          "attachments.0": "Attachments",
          youtube_url: "YouTube link",
          class_id: "Class",
        };
        const humaniseMsg = (rawKey: string, raw: string): string => {
          const label = LABELS[rawKey] ?? rawKey.replace(/_/g, " ");
          return raw
            // "The X field is required." → "X is required."
            .replace(/^The\s+[a-z0-9_. ]+\s+field\s+/i, `${label} `)
            // "The X field must be an array" etc
            .replace(/^The\s+[a-z0-9_. ]+\s+/i, `${label} `);
        };
        const mapped: Record<string, string> = {};
        for (const k of Object.keys(fieldErrors)) {
          const raw = Array.isArray(fieldErrors[k]) ? String(fieldErrors[k][0]) : String(fieldErrors[k]);
          mapped[k] = humaniseMsg(k, raw);
        }
        setErrors((prev) => ({ ...prev, ...mapped }));
      }
      if (status === 403) setServerError(message || "You don't have access to create an assessment for this student or class.");
      else if (status === 422) setServerError("Some fields need correcting. See the highlighted errors below.");
      else setServerError(message || "We couldn't create this assessment. Please try again.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="lg"
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          overflow: "hidden",
          bgcolor: "#fff",
        },
      }}
    >
      {/* Navy gradient header — mirrors admin CreateAssessmentWizard */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_2} 100%)`,
          color: "#fff",
          px: { xs: 2.5, md: 3.5 },
          pt: 2.5,
          pb: 2.5,
          position: "relative",
        }}
      >
        <Typography sx={{ fontSize: 11, letterSpacing: "1px", textTransform: "uppercase", opacity: 0.85, fontWeight: 800 }}>
          Staff Portal · Assessments
        </Typography>
        <Typography sx={{ fontSize: 24, fontWeight: 800, mt: 0.5, letterSpacing: "-0.01em" }}>
          Create New Assessment
        </Typography>
        <Typography sx={{ fontSize: 13, opacity: 0.9, mt: 0.5, maxWidth: 720 }}>
          Create an assessment for students in your teaching scope. Attach learning materials or a YouTube link (optional).
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="Close"
          disabled={submitting}
          sx={{
            position: "absolute", top: 12, right: 12,
            color: "#fff", bgcolor: "rgba(255,255,255,0.15)",
            "&:hover": { bgcolor: "rgba(255,255,255,0.28)" },
            width: 32, height: 32,
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: { xs: 2, md: 3 }, bgcolor: "#f8fafc" }}>
        {optionsError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            Could not load your teaching options. Please refresh and try again.
          </Alert>
        )}
        {serverError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setServerError(null)}>
            {serverError}
          </Alert>
        )}

        {optionsLoading ? (
          <Skeleton variant="rounded" height={360} sx={{ borderRadius: 3 }} />
        ) : teaching.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center", bgcolor: "#fff", borderRadius: 3, border: "1px solid #e2e8f0" }}>
            <MenuBookOutlinedIcon sx={{ fontSize: 44, color: alpha(NAVY, 0.35), mb: 1 }} />
            <Typography sx={{ fontWeight: 800, fontSize: 15, color: NAVY }}>Nothing to assess yet</Typography>
            <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.5 }}>
              You don't currently teach any students in a grade/subject you're assigned to.
              Once a student is assigned, they'll appear here.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {/* Section 1 — Student & academic context */}
            <Grid item xs={12} md={7}>
              <SectionCard title="Academic Context">
                {/* STEP 1 — GRADE */}
                <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.6px", textTransform: "uppercase", color: alpha(NAVY, 0.75) }}>
                  Step 1 · Academic Grade
                </Typography>
                <Box sx={{ mt: 0.75, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {availableGrades.length === 0 ? (
                    <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                      You aren't currently assigned to any teaching grade.
                    </Typography>
                  ) : availableGrades.map((g) => {
                    const active = g.id === selectedGradeId;
                    return (
                      <Chip
                        key={g.id}
                        label={g.name}
                        onClick={() => chooseGrade(g.id)}
                        sx={{
                          fontWeight: 800, fontSize: 12,
                          borderRadius: "8px", height: 30,
                          cursor: "pointer",
                          bgcolor: active ? NAVY : alpha(NAVY, 0.06),
                          color:   active ? "#fff" : NAVY,
                          border:  `1px solid ${active ? NAVY : alpha(NAVY, 0.18)}`,
                          "&:hover": { bgcolor: active ? NAVY_2 : alpha(NAVY, 0.12) },
                        }}
                      />
                    );
                  })}
                </Box>
                {errors.pair && !selectedGradeId && (
                  <Typography sx={{ fontSize: 11.5, color: "error.main", mt: 0.5 }}>{errors.pair}</Typography>
                )}

                {/* STEP 2 — SUBJECT */}
                <Divider sx={{ my: 1.5 }} />
                <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.6px", textTransform: "uppercase", color: alpha(NAVY, 0.75) }}>
                  Step 2 · Subject
                  {selectedGradeId != null && (
                    <Box component="span" sx={{ ml: 0.75, color: "text.secondary", fontWeight: 500, textTransform: "none", letterSpacing: 0 }}>
                      · for {availableGrades.find((g) => g.id === selectedGradeId)?.name}
                    </Box>
                  )}
                </Typography>
                <Box sx={{ mt: 0.75, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {selectedGradeId == null ? (
                    <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                      Choose a grade above to see the subjects you teach for it.
                    </Typography>
                  ) : availableSubjects.length === 0 ? (
                    <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                      No subjects available for this grade in your teaching scope.
                    </Typography>
                  ) : availableSubjects.map((s) => {
                    const active = s.id === selectedSubjectId;
                    return (
                      <Chip
                        key={s.id}
                        label={s.name}
                        onClick={() => chooseSubject(s.pairKey)}
                        sx={{
                          fontWeight: 700, fontSize: 12,
                          borderRadius: "8px", height: 30,
                          cursor: "pointer",
                          bgcolor: active ? NAVY_2 : "#fff",
                          color:   active ? "#fff" : NAVY,
                          border:  `1px solid ${active ? NAVY_2 : alpha(NAVY, 0.22)}`,
                          "&:hover": { bgcolor: active ? NAVY : alpha(NAVY, 0.06) },
                        }}
                      />
                    );
                  })}
                </Box>

                {/* STEP 3 — STUDENTS */}
                <Divider sx={{ my: 1.5 }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.6px", textTransform: "uppercase", color: alpha(NAVY, 0.75) }}>
                    Step 3 · Students
                    {currentPair && (
                      <Box component="span" sx={{ ml: 0.75, color: "text.secondary", fontWeight: 500, textTransform: "none", letterSpacing: 0 }}>
                        · {selectedStudentIds.length} of {currentPair.students.length} selected
                      </Box>
                    )}
                  </Typography>
                  {currentPair && currentPair.students.length > 0 && (
                    <Stack direction="row" spacing={1}>
                      <Button size="small" onClick={selectAllStudents} sx={{ textTransform: "none", fontWeight: 700, color: NAVY }}>Select all</Button>
                      <Button size="small" onClick={clearStudents} sx={{ textTransform: "none", fontWeight: 700, color: "text.secondary" }}>Clear</Button>
                    </Stack>
                  )}
                </Stack>
                {!currentPair ? (
                  <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                    Choose a grade and subject to see eligible students.
                  </Typography>
                ) : currentPair.students.length === 0 ? (
                  <Box sx={{ p: 2, textAlign: "center", bgcolor: "#fff", border: "1px dashed #e2e8f0", borderRadius: 1.5 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>No eligible students</Typography>
                    <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.25 }}>
                      There are no students currently assigned to you for this grade and subject.
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ maxHeight: 260, overflowY: "auto", pr: 1 }}>
                    {currentPair.students.map((s) => {
                      const checked = selectedStudentIds.includes(s.id);
                      return (
                        <FormControlLabel
                          key={s.id}
                          sx={{
                            display: "flex", mx: 0, my: 0.25, py: 0.5, px: 1,
                            borderRadius: 1.5,
                            bgcolor: checked ? alpha(NAVY, 0.05) : "transparent",
                            border: `1px solid ${checked ? alpha(NAVY, 0.25) : "#e5e7eb"}`,
                            "&:hover": { bgcolor: alpha(NAVY, 0.04) },
                          }}
                          control={<Checkbox size="small" checked={checked} onChange={() => toggleStudent(s.id)} sx={{ color: NAVY, "&.Mui-checked": { color: NAVY } }} />}
                          label={
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                              <Box sx={{
                                width: 30, height: 30, borderRadius: "50%",
                                bgcolor: alpha(NAVY, 0.12), color: NAVY,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: 11, fontWeight: 800,
                              }}>
                                {s.initials || <PersonOutlinedIcon fontSize="small" />}
                              </Box>
                              <Box>
                                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{s.name}</Typography>
                                <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{s.grade ?? "—"}</Typography>
                              </Box>
                            </Stack>
                          }
                        />
                      );
                    })}
                  </Box>
                )}
                {errors.student_ids && (
                  <Typography sx={{ fontSize: 11.5, color: "error.main", mt: 0.5 }}>{errors.student_ids}</Typography>
                )}
              </SectionCard>
            </Grid>

            <Grid item xs={12} md={5}>
              <SectionCard title="Assessment Details">
                <TextField
                  fullWidth size="small" label="Assessment Title *" placeholder="e.g. Weekly Quiz — Unit 3"
                  value={title} onChange={(e) => setTitle(e.target.value)}
                  error={!!errors.title} helperText={errors.title || " "}
                />
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "text.secondary", mt: 0.5, mb: 0.75 }}>
                  Assessment Type *
                </Typography>
                <ToggleButtonGroup
                  value={assessmentType} exclusive
                  onChange={(_, v) => v && setAssessmentType(v)}
                  size="small"
                  sx={{
                    flexWrap: "wrap", gap: 0.5,
                    "& .MuiToggleButton-root.Mui-selected": {
                      bgcolor: NAVY, color: "#fff",
                      "&:hover": { bgcolor: NAVY_2 },
                    },
                  }}
                >
                  {types.map((t) => (
                    <ToggleButton key={t.value} value={t.value} sx={{ textTransform: "none", fontWeight: 700 }}>
                      {t.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                {errors.assessment_type && (
                  <Typography sx={{ fontSize: 11.5, color: "error.main", mt: 0.5 }}>{errors.assessment_type}</Typography>
                )}
                <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                  <Grid item xs={6}>
                    {/* Premium AppDatePicker (shared MUI X wrapper).
                        Contract preserved: value in/out is "YYYY-MM-DD"
                        with no timezone shift — same string the assessment
                        payload has always sent. Clearable turned OFF
                        because Assessment Date is required. */}
                    <AppDatePicker
                      fullWidth size="small"
                      label="Assessment Date *"
                      value={date}
                      onChange={(v) => setDate(v)}
                      error={!!errors.date}
                      helperText={errors.date || " "}
                      clearable={false}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    {/* Due Date optional — clearable stays default (on)
                        so staff can null it out. minDate pinned to
                        Assessment Date so the picker won't offer a Due
                        Date earlier than the assessment itself; this
                        matches the frontend-only relationship — backend
                        currently does NOT enforce Due >= Assessment
                        Date (see report §14). */}
                    <AppDatePicker
                      fullWidth size="small"
                      label="Due Date"
                      value={dueDate}
                      onChange={(v) => setDueDate(v)}
                      helperText="Optional"
                      minDate={date || undefined}
                    />
                  </Grid>
                </Grid>
              </SectionCard>

              <SectionCard title="Scoring" sx={{ mt: 2 }}>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth size="small" type="number" label="Maximum Score *"
                      inputProps={{ min: 1, max: 1000 }}
                      value={worth} onChange={(e) => setWorth(e.target.value)}
                      error={!!errors.worth} helperText={errors.worth || " "}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth size="small" type="number"
                      label={currentType?.numeric === false ? "Pass % (n/a)" : "Pass %"}
                      disabled={currentType?.numeric === false}
                      inputProps={{ min: 0, max: 100 }}
                      value={currentType?.numeric === false ? "" : passThreshold}
                      onChange={(e) => setPassThreshold(e.target.value)}
                      error={!!errors.pass_threshold}
                      helperText={errors.pass_threshold || (currentType?.numeric === false ? "Observation — no pass %" : " ")}
                    />
                  </Grid>
                </Grid>
              </SectionCard>

              <SectionCard title="Learning Materials" sx={{ mt: 2 }}>
                {/* File dropzone — supports drag-and-drop AND click.
                    Client-side ext/size gate mirrors backend allow-list
                    so obviously-wrong picks are refused before upload
                    even starts. Backend re-validates authoritatively. */}
                <Box
                  onDragOver={(e) => { e.preventDefault(); setDropActive(true); }}
                  onDragLeave={() => setDropActive(false)}
                  onDrop={(e) => {
                    e.preventDefault(); setDropActive(false);
                    const dropped = Array.from(e.dataTransfer.files || []);
                    handlePickFiles(dropped);
                  }}
                  onClick={() => document.getElementById("staff-assess-file-input")?.click()}
                  sx={{
                    border: `2px dashed ${dropActive ? NAVY : alpha(NAVY, 0.30)}`,
                    borderRadius: 2, p: 2, textAlign: "center",
                    bgcolor: dropActive ? alpha(NAVY, 0.06) : alpha(NAVY, 0.03),
                    cursor: "pointer",
                    transition: "background-color 160ms ease, border-color 160ms ease",
                  }}
                >
                  <CloudUploadOutlinedIcon sx={{ fontSize: 32, color: NAVY, opacity: 0.75 }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: NAVY, mt: 0.5 }}>
                    Drag &amp; drop files here or click to choose
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.25 }}>
                    PDF, Word, PowerPoint, Excel, images — max 10 MB each
                  </Typography>
                  <input
                    id="staff-assess-file-input"
                    type="file" multiple hidden
                    onChange={(e) => handlePickFiles(Array.from(e.target.files || []))}
                  />
                </Box>
                {attachedFiles.length > 0 && (
                  <Stack spacing={0.75} sx={{ mt: 1.25 }}>
                    {attachedFiles.map((f, i) => (
                      <Stack key={`${f.name}-${i}`} direction="row" alignItems="center" spacing={1}
                        sx={{ p: 0.75, borderRadius: 1.5, border: "1px solid #e5e7eb", bgcolor: "#fff" }}
                      >
                        <Box sx={{ color: NAVY, display: "flex" }}>{fileIcon(f)}</Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {f.name}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                            {(f.size / 1024).toFixed(0)} KB
                          </Typography>
                        </Box>
                        <IconButton size="small" aria-label="Remove file"
                          onClick={() => setAttachedFiles((prev) => prev.filter((_, idx) => idx !== i))}
                        >
                          <DeleteOutlineOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    ))}
                  </Stack>
                )}
                <TextField
                  fullWidth size="small" sx={{ mt: 1.5 }}
                  label="YouTube Video URL"
                  placeholder="https://www.youtube.com/watch?v=… or https://youtu.be/…"
                  value={youtubeUrl}
                  onChange={(e) => setYoutubeUrl(e.target.value)}
                  error={!!errors.youtube_url}
                  helperText={errors.youtube_url || "Optional — only youtube.com and youtu.be links"}
                  InputProps={{
                    startAdornment: (
                      <SmartDisplayOutlinedIcon sx={{ fontSize: 18, color: alpha(NAVY, 0.55), mr: 1 }} />
                    ),
                  }}
                />
                {youtubeUrl.trim() && (
                  <Box sx={{ mt: 1, p: 1, borderRadius: 1.5, border: "1px solid #e5e7eb", bgcolor: alpha("#EF0000", 0.03), display: "flex", alignItems: "center", gap: 1 }}>
                    <SmartDisplayOutlinedIcon sx={{ color: "#DC2626" }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}>YouTube video attached</Typography>
                      <Typography sx={{ fontSize: 11, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {youtubeUrl.trim()}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </SectionCard>

              <SectionCard title="Notes" sx={{ mt: 2 }}>
                <TextField
                  fullWidth size="small" label="Focus / Learning Goal"
                  placeholder="e.g. Reading"
                  value={focus} onChange={(e) => setFocus(e.target.value)}
                  helperText="Optional learning objective or focus area"
                />
                <TextField
                  fullWidth multiline minRows={3} size="small" label="Instructions for Students"
                  value={instructions} onChange={(e) => setInstructions(e.target.value)}
                  sx={{ mt: 1.5 }}
                  helperText="Optional instructions visible with this assessment"
                />
              </SectionCard>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      {/* Footer — mirrors admin wizard footer: navy primary, neutral cancel */}
      <Box sx={{ px: { xs: 2, md: 3 }, py: 2, borderTop: "1px solid #e2e8f0", bgcolor: "#f8fafc" }}>
        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          <Button
            variant="outlined" onClick={onClose} disabled={submitting}
            sx={{
              textTransform: "none", fontWeight: 700, borderRadius: 2,
              color: "#475569", borderColor: "#cbd5e1",
              "&:hover": { borderColor: "#94a3b8", bgcolor: "rgba(148,163,184,0.08)" },
              minWidth: 120,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<AddTaskOutlinedIcon />}
            disabled={submitting || optionsLoading || teaching.length === 0}
            onClick={handleSubmit}
            sx={{
              textTransform: "none", fontWeight: 800, borderRadius: 2,
              background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_2} 100%)`,
              boxShadow: "0 4px 14px rgba(27,58,107,0.35)",
              "&:hover": { boxShadow: "0 6px 18px rgba(27,58,107,0.45)", background: `linear-gradient(135deg, ${NAVY_2} 0%, ${NAVY} 100%)` },
              minWidth: 200,
            }}
          >
            {submitting ? "Creating…" : "Create Assessment"}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
};

/** Small helper for the sectioned white cards used inside the dialog body. */
const SectionCard: React.FC<{ title: string; sx?: any; children: React.ReactNode }> = ({ title, sx, children }) => (
  <Box sx={{ bgcolor: "#fff", border: "1px solid #e2e8f0", borderRadius: 2, p: 2, ...sx }}>
    <Typography sx={{ fontSize: 11.5, fontWeight: 800, letterSpacing: "0.8px", textTransform: "uppercase", color: NAVY, mb: 1.5 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

export default StaffAssessmentDialog;
